import React, {useCallback, useEffect, useState} from 'react'
import {convertToHours, formatDateToDDMMYYYY} from '../../common-function/formatDateToYearMonth'
import {KTIcon} from '../../../_metronic/helpers'
import FeedbackFilter from './FeedbackFilter'
import FeedbackDetails from './FeedbackDetails'
import Pagination from '../../common-function/Pagination'
import {restRequest} from '../../../api-utils/apiQueries'
import {
  formatPreferredContactTime,
  getPreferredContactTime,
} from './feedbackContactTime'
import {appendDateFilterToBody} from '../../common-function/adminDateFilterPayload'
import {useAdminListState} from '../../common-function/useAdminListState'
import ListFilterToolbar from '../../common-function/ListFilterToolbar'
import ListPageHeader from '../../common-function/ListPageHeader'
import {
  buildFeedbackFilterSummary,
  getDefaultFeedbackFilter,
  FeedbackFilterState,
} from './feedbackDateRange'

const LIST_STORAGE_KEY = 'feedback-response'

const getRatingMeta = (value: unknown) => {
  const rating = Number(value)
  if (!Number.isFinite(rating) || rating <= 0) {
    return {label: '—', badge: 'badge-light-secondary', text: 'text-muted'}
  }
  if (rating >= 4) {
    return {label: `${rating}/5`, badge: 'badge-light-success', text: 'text-success'}
  }
  if (rating >= 3) {
    return {label: `${rating}/5`, badge: 'badge-light-warning', text: 'text-warning'}
  }
  return {label: `${rating}/5`, badge: 'badge-light-danger', text: 'text-danger'}
}

const RatingBadge = ({value}: {value: unknown}) => {
  const meta = getRatingMeta(value)
  return (
    <span className={`badge ${meta.badge} fw-bold`}>
      <KTIcon iconName='star' className={`fs-7 me-1 ${meta.text}`} />
      {meta.label}
    </span>
  )
}

const FeedbackResponseWrapper = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [feedbacks, setFeedbacks] = useState<any[]>([])
  const [showFilter, setShowFilter] = useState<boolean>(false)
  const [totalPages, setTotalPages] = useState(0)
  const [totalRecords, setTotalRecords] = useState(0)
  const [showDetails, setShowDetails] = useState(false)
  const [selectedId, setSelectedId] = useState<number | null>(null)

  const {
    page,
    setPage,
    searchQuery,
    setSearchQuery,
    activeFilter,
    applyFilter,
    resetToDefault,
    resetPageForSearch,
    isFilterActive,
  } = useAdminListState<FeedbackFilterState>({
    storageKey: LIST_STORAGE_KEY,
    getDefaultFilter: getDefaultFeedbackFilter,
    isDefaultFilter: (f, search) => f.preset === 'today' && !search.trim(),
  })

  const fetchFeedbackData = useCallback(
    async (query: string) => {
      setIsLoading(true)
      const bodyData: Record<string, unknown> = {page}
      appendDateFilterToBody(bodyData, activeFilter)
      if (query.trim()) bodyData.search = query.trim()

      try {
        const response: any = await restRequest.getFeedbackAllData(bodyData)
        const data = response?.data?.data
        setFeedbacks(data?.data ?? [])
        setTotalPages(data?.pager?.totalPages ?? 0)
        setTotalRecords(data?.pager?.totalRecords ?? 0)
      } catch (error) {
        console.error('Failed to load feedback:', error)
        setFeedbacks([])
        setTotalPages(0)
        setTotalRecords(0)
      } finally {
        setIsLoading(false)
      }
    },
    [page, activeFilter]
  )

  useEffect(() => {
    fetchFeedbackData(searchQuery)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, activeFilter])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    if (e.target.value === '') {
      resetPageForSearch()
      fetchFeedbackData('')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      resetPageForSearch()
      fetchFeedbackData(searchQuery)
    }
  }

  const submitSearch = () => {
    resetPageForSearch()
    fetchFeedbackData(searchQuery)
  }

  const handleFeedbackDetails = (id: number) => {
    setSelectedId(id)
    setShowDetails(true)
  }

  const filterSummary = buildFeedbackFilterSummary(
    activeFilter,
    searchQuery,
    totalRecords
  )

  return (
    <div>
      <div className='d-flex flex-column flex-md-row justify-content-between align-items-md-end gap-3 mb-2'>
        <div>
          <p className='card-label fw-bold fs-3 mb-1'>Feedback Response</p>
          <div className='text-muted fs-7'>
            Review app feedback, contact preferences, and satisfaction signals.
          </div>
        </div>
        <span className='badge badge-light-primary fw-semibold py-3 px-4'>
          {totalRecords} feedback{totalRecords === 1 ? '' : 's'}
        </span>
      </div>

      <ListFilterToolbar
        filterSummary={filterSummary}
        showReset={isFilterActive}
        onReset={resetToDefault}
      />

      <div className='card'>
        <ListPageHeader
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          onSearchKeyDown={handleKeyDown}
          onSearchSubmit={submitSearch}
          searchPlaceholder='Search by name, email, or phone'
          isLoading={isLoading}
          isFilterActive={isFilterActive}
          onOpenFilter={() => setShowFilter(true)}
          showExport={false}
        />
        <div className='card-body py-3'>
          {isLoading ? (
            <div className='d-flex flex-column align-items-center justify-content-center py-15'>
              <div className='spinner-border text-primary mb-4'></div>
              <div className='text-muted fw-semibold'>Loading feedback responses...</div>
            </div>
          ) : (
            <div className='table-responsive'>
              {feedbacks.length > 0 ? (
                <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3'>
                  <thead>
                    <tr className='fw-bold text-muted'>
                      <th className='min-w-260px'>User</th>
                      <th className='min-w-120px'>Contact</th>
                      <th className='min-w-160px'>Preferred contact time</th>
                      <th className='min-w-120px'>Experience</th>
                      <th className='min-w-120px'>Satisfaction</th>
                      <th className='min-w-120px'>Submitted</th>
                      <th className='min-w-80px text-end'>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feedbacks.map((item: any) => (
                      <tr key={item.id}>
                        <td>
                          <div className='d-flex align-items-center gap-3'>
                            <div className='symbol symbol-40px flex-shrink-0'>
                              <div className='symbol-label bg-light-primary text-primary fw-bold'>
                                {String(item.user?.name ?? '?').charAt(0).toUpperCase()}
                              </div>
                            </div>
                            <div className='min-w-0'>
                              <p className='text-dark fw-bold text-hover-primary fs-6 mb-1'>
                                {item.user?.name ?? '—'}
                              </p>
                              <div className='text-muted fs-7 text-truncate mw-250px'>
                                {item.user?.email ?? '—'}
                              </div>
                              <div className='text-muted fs-7'>{item.user?.mobile ?? '—'}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              item.can_we_connect ? 'badge-light-success' : 'badge-light-secondary'
                            }`}
                          >
                            {item.can_we_connect ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className='text-dark fw-bold text-hover-primary fs-6'>
                          {item.can_we_connect
                            ? formatPreferredContactTime(getPreferredContactTime(item))
                            : '—'}
                        </td>
                        <td>
                          <RatingBadge value={item.overall_experience} />
                        </td>
                        <td>
                          <RatingBadge value={item.overall_satisfaction} />
                        </td>
                        <td>
                          {item.createdAt ? (
                            <>
                              <p className='text-dark fw-bold text-hover-primary d-block mb-0 fs-6'>
                                {formatDateToDDMMYYYY(item.createdAt)}
                              </p>
                              <span className='text-muted fs-7'>
                                {convertToHours(new Date(item.createdAt))}
                              </span>
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className='text-end'>
                          <button
                            type='button'
                            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                            title='View feedback details'
                            onClick={() => handleFeedbackDetails(item.id)}
                          >
                            <KTIcon iconName='eye' className='fs-1' />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className='text-center py-12'>
                  <div className='symbol symbol-60px mb-4'>
                    <div className='symbol-label bg-light-primary'>
                      <KTIcon iconName='message-text-2' className='fs-1 text-primary' />
                    </div>
                  </div>
                  <div className='fw-bold text-gray-900 fs-5 mb-1'>No feedback found</div>
                  <div className='text-muted fs-7'>
                    Try a different date range or clear the search term.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {totalPages > 1 && (
        <Pagination totalPages={totalPages} page={page} setPage={setPage} />
      )}

      {showFilter && (
        <FeedbackFilter
          showFilter={showFilter}
          toggle={() => setShowFilter(false)}
          activeFilter={activeFilter}
          onApply={applyFilter}
        />
      )}

      {showDetails && selectedId != null && (
        <FeedbackDetails
          showDetails={showDetails}
          toggle={() => setShowDetails(false)}
          selectedId={selectedId}
        />
      )}
    </div>
  )
}

export default FeedbackResponseWrapper
